import React, { useContext, useEffect, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "./Card";
import ActivityFeedSkeleton, {
	ActivityFeedContentSkeleton,
} from "./skeletons/ActivityFeedSkeleton";
import anonUser from "../../../../assets/images/userGrey.png";
import OptionsSelector from "./OptionsSelector";
import OrgApi from "../../../../API/OrgApi";
import { UserContext } from "../../../../App";
import { formatDistanceToNowStrict } from "date-fns";
import MapIconApi from "../../../../API/MapIconApi";
import { Info } from "lucide-react";
import { Pagination, PaginationItem } from "@mui/material";
import { BiLeftArrow, BiRightArrow } from "react-icons/bi";

export default function ActivityFeed() {
	const [mapIcons, setMapIcons] = useState([]);
	const [data, setData] = useState([]);
	const [init, setInit] = useState(false);
	const [loading, setLoading] = useState(true);
	const [page, setPage] = useState(1);
	const [totalPages, setTotalPages] = useState(0);
	const user = useContext(UserContext);
	const [activeTab, setActiveTab] = useState(
		user.userType === "SalesRep" ? "MY_ACTIVITY" : "ALL",
	);

	useEffect(() => {
		MapIconApi.GetAllMapIcons().then((res) => setMapIcons(res.data));
	}, []);

	useEffect(() => {
		const fetchData = async () => {
			setLoading(true);
			const res = await OrgApi.getDashboardActivitiesData({
				salesRepId: activeTab === "ALL" ? undefined : user.id,
				salesOrgId:
					activeTab === "ALL"
						? user.userType === "SalesOrg"
							? user.id
							: undefined
						: undefined,
				pagingParams: {
					pageNumber: page,
				},
			});

			setData(res.data.list);
			setTotalPages(res.data.totalPages || 1);
			setLoading(false);
		};

		fetchData().finally(() => setInit(true));
	}, [activeTab, user.id, page]);

	const extractNotesArray = (acHistory) =>
		acHistory.notes?.split("!*!").filter((note) => note !== "") ?? [];

	const getIconSource = (acHistory) => {
		switch (acHistory.activityType) {
			case "Map Icon Change":
				const toBase64 = mapIcons.find(
					(mi) => mi.id === acHistory.iconToId,
				)?.image;
				return toBase64 ? `data:image/png;base64,${toBase64}` : undefined;
			case "OwnerShip Change":
			case "Lead Edit":
				return acHistory.salesRepImage;
			case "Lead Create":
			default:
				const fromBase64 = mapIcons.find(
					(mi) => mi.id === acHistory.iconFromId,
				)?.image;
				return fromBase64 ? `data:image/png;base64,${fromBase64}` : undefined;
		}
	};

	const getTitle = (acHistory) => {
		switch (acHistory.activityType) {
			case "Map Icon Change":
				return mapIcons.find((mi) => mi.id === acHistory.iconToId)?.name;
			case "OwnerShip Change":
			case "Lead Edit":
				return acHistory.salesRepName;
			case "Lead Create":
			default:
				return mapIcons.find((mi) => mi.id === acHistory.iconFromId)?.name;
		}
	};

	const getLine1 = (acHistory) => {
		switch (acHistory.activityType) {
			case "Map Icon Change":
				return "Status Update";
			case "Lead Create":
				return "Lead Created";
			case "OwnerShip Change":
				return "Ownership Change";
			case "Lead Edit":
			default:
				return "Lead Edited";
		}
	};

	const getLine2 = (acHistory) =>
		acHistory.salesOrgName ?? "No Sales Org Found";

	const getLine3 = (acHistory) => {
		switch (acHistory.activityType) {
			case "Map Icon Change":
				const mapIconFromImage = mapIcons.find(
					(icons) => icons.id === acHistory.iconFromId,
				)?.image;
				const mapIconToImage = mapIcons.find(
					(icons) => icons.id === acHistory.iconToId,
				)?.image;
				return (
					<span className="flex gap-1.5 items-center">
						Icon changed from
						{mapIconFromImage ? (
							<img
								width="25px"
								height="25px"
								src={"data:image/png;base64," + mapIconFromImage}
								alt="Map Icon"
							/>
						) : (
							<Info className="text-gray-500" size={25} />
						)}
						to
						{mapIconToImage ? (
							<img
								width="25px"
								height="25px"
								src={"data:image/png;base64," + mapIconToImage}
								alt="Map Icon"
							/>
						) : (
							<Info className="text-gray-500" size={25} />
						)}
					</span>
				);
			case "Lead Create":
				return "";
			case "OwnerShip Change":
				return acHistory.notes;
			case "Lead Edit":
				return extractNotesArray(acHistory).map((n, i) => (
					<p key={i}>Notes Updated</p>
				));
			default:
				return extractNotesArray(acHistory).map((n, i) => <p key={i}>{n}</p>);
		}
	};

	const getLine4 = (acHistory) => {
		return acHistory.activityType !== "OwnerShip Change"
			? acHistory.salesRepName ?? "No Sales Rep Found"
			: "";
	};

	if (!init) return <ActivityFeedSkeleton />;

	return (
		<Card className="max-h-[800px] flex flex-col relative">
			<CardHeader className="py-5 px-6 flex justify-between items-center border-b border-b-neutral-200">
				<CardTitle className="text-base font-medium">Activity</CardTitle>
				{user.userType !== "SalesRep" && (
					<OptionsSelector
						options={[
							{ label: "All", value: "ALL" },
							{ label: "My Activity", value: "MY_ACTIVITY" },
						]}
						onSelect={setActiveTab}
						selected={activeTab}
						detached
					/>
				)}
			</CardHeader>
			{loading ? (
				<ActivityFeedContentSkeleton />
			) : (
				<CardContent className="p-4 lg:p-4 flex flex-col gap-2.5 lg:gap-5 overflow-y-auto flex-1">
					{data.map((item, i) => (
						<React.Fragment key={item.id}>
							<div className="flex gap-3">
								<img
									src={getIconSource(item) ?? anonUser}
									style={{
										width: 48,
										height: 48,
										borderRadius: "50%",
									}}
									alt={getTitle(item)}
									onError={() => {
										setData((prevData) =>
											prevData.map((dataItem) =>
												dataItem.id === item.id
													? { ...dataItem, salesRepImage: undefined }
													: dataItem,
											),
										);
									}}
								/>
								<div>
									<div className="flex items-center gap-2">
										<span className="font-medium">{getTitle(item)}</span>
										<span className="text-xs text-muted-foreground">
											{formatDistanceToNowStrict(new Date(item.dateCreated), {
												addSuffix: true,
											})}
										</span>
									</div>
									<p className="text-gray-600 text-sm">{getLine1(item)}</p>
									<p className="text-gray-600 text-sm">{getLine4(item)}</p>
									<p className="text-gray-600 text-sm">{getLine2(item)}</p>
									<p className="text-gray-600 text-sm">{getLine3(item)}</p>
								</div>
							</div>
							{i < data.length - 1 && <hr />}
						</React.Fragment>
					))}
				</CardContent>
			)}
			{totalPages > 1 && (
				<div className="py-10">
					<div className="flex justify-center items-center absolute bottom-4 left-0 right-0">
						<Pagination
							color="light"
							renderItem={(item) => (
								<PaginationItem
									components={{
										next: (props) => (
											<button {...props}>
												<BiRightArrow color="var(--color-icon)" />
											</button>
										),
										previous: (props) => (
											<button {...props}>
												<BiLeftArrow color="var(--color-icon)" />
											</button>
										),
									}}
									{...item}
									sx={{ color: "white" }}
								/>
							)}
							count={totalPages}
							page={page}
							onChange={(e, v) => setPage(v)}
						/>
					</div>
				</div>
			)}
		</Card>
	);
}
